import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import { FaTrash } from 'react-icons/fa';
import { Button, Card, Col, OverlayTrigger, Row, Spinner, Tooltip } from 'react-bootstrap';
import AppContext from 'context/Context';
import { FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';

const ProductoCard = (
    {
        producto,
        index,
        actualizarCampo,
        eliminarProducto,
    }


) => {
    const { config: { isDark } } = useContext(AppContext);



    /**
     * Calculos de productos
     */
    const imagenSrc = producto.vImagenProducto_Base64 
        ? `data:image/jpeg;base64,${producto.vImagenProducto_Base64}` 
        : "";

    const precioUnitario = producto.dPrecioUnitario;
    const cantidad = producto.dCantidad;
    const tipoFactor = producto.vTipoFactorIVA;
    const tasa = producto.dTasaCuotaIVA;
    const importe = cantidad * precioUnitario;
    let iva = 0;
    if (tipoFactor === "Tasa") {
        iva = importe * tasa;
    } else if (tipoFactor === "Cuota") {
        iva = tasa;
    }
    const total = importe + iva;



    // Estilos personalizados para inputs de Material-UI en modo claro/oscuro
    const muiInputStyles = {
        '& .MuiOutlinedInput-root': {
            backgroundColor: isDark ? '#121416' : '#fff',
            color: isDark ? '#fff' : '#000',
            '& fieldset': {
                borderColor: isDark ? '#3b424a' : '#d8e2ef',
            },
            '&:hover fieldset': {
                borderColor: isDark ? '#4b5563' : '#a2b4cd',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#009bdb',
            },
            '&.Mui-disabled fieldset': {
                borderColor: isDark ? '#2a2d31' : '#e0e0e0',
            },
            '& input': {
                color: isDark ? '#fff !important' : '#000 !important',
                WebkitTextFillColor: isDark ? '#fff !important' : '#000 !important',
            },
            '&.Mui-disabled input': {
                color: isDark ? '#7f8c8d !important' : '#a0a0a0 !important',
                WebkitTextFillColor: isDark ? '#7f8c8d !important' : '#a0a0a0 !important',
            }
        },
        '& .MuiInputLabel-root': {
            color: isDark ? '#e4e6eb !important' : '#2c3e50 !important',
            '&.Mui-focused': {
                color: '#009bdb !important',
            },
            '&.Mui-disabled': {
                color: isDark ? '#7f8c8d !important' : '#a0a0a0 !important',
                WebkitTextFillColor: isDark ? '#7f8c8d !important' : '#a0a0a0 !important',
            }
        },
        '& .MuiSelect-icon': {
            color: isDark ? '#e4e6eb !important' : '#2c3e50 !important',
        },
        '& .MuiSelect-select': {
            color: isDark ? '#fff !important' : '#000 !important',
            '&.Mui-disabled': {
                color: isDark ? '#7f8c8d !important' : '#a0a0a0 !important',
                WebkitTextFillColor: isDark ? '#7f8c8d !important' : '#a0a0a0 !important',
            }
        }
    };

    const TooltipButton = ({
        message,
        variant,
        onClick,
        disabled,
        isLoading,
        showText = false,
        children,
        style = {},
    }) => (
        <OverlayTrigger
            placement="top"
            overlay={<Tooltip>{message}</Tooltip>}
        >
            <Button
                size="sm"
                variant={variant}
                onClick={onClick}
                disabled={disabled}
                style={{ width: "100%", display: "block", ...style }}
            >
                {isLoading ? (
                    <Spinner animation="border" size="sm" className="me-2" />
                ) : (
                    <>
                        {children}
                        {showText && <span className="ms-2">{message}</span>}
                    </>
                )}
            </Button>
        </OverlayTrigger>
    );

    // Agregar PropTypes
    TooltipButton.propTypes = {
        message: PropTypes.string.isRequired,
        variant: PropTypes.string.isRequired,
        onClick: PropTypes.func,
        disabled: PropTypes.bool,
        isLoading: PropTypes.bool,
        children: PropTypes.node,
        showText: PropTypes.bool,
        style: PropTypes.object,
        fullWidth: PropTypes.bool,
    };

    // Valores por defecto
    TooltipButton.defaultProps = {
        onClick: () => { },
        disabled: false,
        isLoading: false,
        showText: false,
        style: {},
        fullWidth: false,
    };


    return (
        <Card
            key={index}
            className="mb-3 border border-200 shadow-sm"
            style={{
                borderRadius: '8px',
            }}
        >
            {/* Barra superior */}
            <div
                className="bg-primary"
                style={{
                    height: '3px',
                    borderRadius: '8px 8px 0 0',
                }}
            />

            <Card.Body className="p-3">
                {/* Header con imagen, título y botón eliminar */}
                <Row className="align-items-center">
                    {/* Imagen del producto */}
                    <Col xs={3} sm={2} md={1} className="text-center pe-0 d-none d-sm-block">
                        <div
                            className="border border-200 d-flex align-items-center justify-content-center bg-200 text-600"
                            style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '8px',
                                overflow: 'hidden',
                            }}
                        >
                            {imagenSrc ? (
                                <img
                                    src={imagenSrc}
                                    alt="Producto"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                            ) : (
                                <span className="fs-1 fw-bold">
                                    {producto.vNombreProducto ? producto.vNombreProducto.substring(0, 2).toUpperCase() : 'NA'}
                                </span>
                            )}
                        </div>
                    </Col>

                    {/* Información del producto */}
                    <Col xs={12} sm={8} md={10}>
                        <Row>
                            <Col xs={12} sm={6} md={3} className="mb-3">
                                <TextField
                                    label="Código de Barras"
                                    size="small"
                                    value={producto.vCodigoBarras}
                                    onChange={(e) => {
                                        actualizarCampo(producto.idUnica, 'vCodigoBarras', e.target.value);
                                    }}
                                    fullWidth
                                    inputProps={{
                                        style: { fontSize: '0.7rem' }
                                    }}
                                    sx={muiInputStyles}
                                />
                            </Col>
                            <Col xs={12} sm={6} md={3} className="mb-3">
                                <TextField
                                    label="Clave Unidad"
                                    size="small"
                                    value={producto.vClaveUnidadSat}
                                    onChange={(e) => {
                                        actualizarCampo(producto.idUnica, 'vClaveUnidadSat', e.target.value);
                                    }}
                                    fullWidth
                                    inputProps={{
                                        style: { fontSize: '0.85rem' }
                                    }}
                                    sx={muiInputStyles}
                                />
                            </Col>
                            <Col xs={12} sm={6} md={3} className="mb-3">
                                <TextField
                                    label="Código SAT"
                                    size="small"
                                    value={producto.vCodigoProductoServicioSat}
                                    onChange={(e) => {
                                        actualizarCampo(producto.idUnica, 'vCodigoProductoServicioSat', e.target.value);
                                    }}
                                    fullWidth
                                    inputProps={{
                                        style: { fontSize: '0.85rem' }
                                    }}
                                    sx={muiInputStyles}
                                />
                            </Col>
                            <Col xs={12} sm={6} md={3} className="mb-1">
                                {/* 6. Tipo Factor IVA */}
                                <FormControl size="small" fullWidth sx={muiInputStyles}>
                                    <InputLabel id={`tipo-factor-label-${index}`}>Tipo Factor</InputLabel>
                                    <Select
                                        labelId={`tipo-factor-label-${index}`}
                                        label="Tipo Factor"
                                        value={tipoFactor}
                                        onChange={(e) => actualizarCampo(producto.idUnica, 'vTipoFactorIVA', e.target.value)}
                                        sx={{ fontSize: '0.85rem' }}
                                    >
                                        <MenuItem value={"Tasa"}>Tasa</MenuItem>
                                        <MenuItem value={"Cuota"}>Cuota</MenuItem>
                                        <MenuItem value={"Exento"}>Exento</MenuItem>
                                    </Select>
                                </FormControl>
                            </Col>
                        </Row>
                        <Row className="mb-1">
                            <Col xs={12}>
                                <TextField
                                    label="Descripción"
                                    size="small"
                                    value={producto.vNombreProducto}
                                    onChange={(e) => {
                                        actualizarCampo(producto.idUnica, 'vNombreProducto', e.target.value);
                                    }}
                                    fullWidth
                                    multiline
                                    rows={3}
                                    inputProps={{
                                        style: { fontSize: '0.95rem', fontWeight: 'bold' }
                                    }}
                                    sx={muiInputStyles}
                                />
                            </Col>
                        </Row>
                    </Col>

                    {/* Botón eliminar */}
                    <Col xs={0} sm={2} md={1} className="text-end ps-0">
                        <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => eliminarProducto(index)}
                            className="d-none d-sm-block"
                            style={{
                                borderRadius: '6px',
                                width: '36px',
                                height: '36px',
                                padding: '0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <FaTrash size={14} />
                        </Button>
                    </Col>
                </Row>

                {/* Divisor */}
                <hr className="my-2 border-200" />

                {/* FILA ÚNICA: Todos los campos en orden solicitado */}
                <Row className="g-2">
                    {/* 3. Cantidad */}
                    <Col xs={12} sm={4} md={4} xl={1}>
                        <TextField
                            label="Cant."
                            type="number"
                            size="small"
                            value={cantidad}
                            onChange={(e) => actualizarCampo(producto.idUnica, 'dCantidad', parseFloat(e.target.value) || 0)}
                            fullWidth
                            inputProps={{
                                min: 0,
                                step: "any",
                                style: { fontSize: '0.85rem' }
                            }}
                            sx={muiInputStyles}
                        />
                    </Col>

                    {/* 4. Precio Unitario */}
                    <Col xs={12} sm={4} md={4} xl={2}>
                        <TextField
                            label="Precio Unitario"
                            type="number"
                            size="small"
                            value={precioUnitario}
                            onChange={(e) => actualizarCampo(producto.idUnica, 'dPrecioUnitario', parseFloat(e.target.value) || 0)}
                            fullWidth
                            inputProps={{
                                min: 0,
                                step: "any",
                                style: { fontSize: '0.9rem', fontWeight: '500' }
                            }}
                            sx={muiInputStyles}
                        />
                    </Col>

                    {/* 5. Importe */}
                    <Col xs={12} sm={4} md={4} xl={2}>
                        <TextField
                            label="Importe"
                            type="number"
                            size="small"
                            value={importe.toFixed(2)}
                            fullWidth
                            inputProps={{
                                readOnly: true,
                                style: {
                                    fontSize: '0.9rem',
                                    fontWeight: '600',
                                    color: 'inherit',
                                },
                            }}
                            sx={muiInputStyles}
                        />
                    </Col>


                    {/* 7. Tasa/Cuota IVA */}
                    <Col xs={12} sm={4} md={4} xl={2}>
                        <TextField
                            label="Tasa/Cuota"
                            type="number"
                            size="small"
                            value={producto.vTipoFactorIVA === "Exento" ? 0 : tasa}
                            onChange={(e) => {
                                const nuevoValor = e.target.value;
                                actualizarCampo(
                                    producto.idUnica,
                                    'dTasaCuotaIVA',
                                    nuevoValor === "" ? "" : parseFloat(nuevoValor)
                                );
                            }}
                            fullWidth
                            onKeyDown={(e) => {
                                if (e.key === "-" || e.key === "e" || e.key === "E") {
                                    e.preventDefault();
                                }
                            }}
                            inputProps={{
                                min: 0,
                                step: "any",
                                style: { fontSize: '0.85rem' }
                            }}
                            sx={muiInputStyles}
                            disabled={producto.vTipoFactorIVA === "Exento"}
                        />
                    </Col>

                    {/* 8. IVA */}
                    <Col xs={12} sm={4} md={4} xl={2}>
                        <TextField
                            label="IVA"
                            type="text"
                            size="small"
                            value={iva.toFixed(2)}
                            fullWidth
                            inputProps={{
                                readOnly: true,
                                style: {
                                    fontSize: '0.9rem',
                                    fontWeight: '600'
                                },
                            }}
                            sx={muiInputStyles}
                        />
                    </Col>

                    {/* 9. Total */}
                    <Col xs={12} sm={4} md={4} xl={2} className="d-flex justify-content-between align-items-center">
                        <div className="bg-primary rounded-2 p-2 flex-grow-1">
                            <span className="text-white fw-bold fs-0">
                                ${total.toFixed(2)}
                            </span>
                        </div>
                        <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => eliminarProducto(index)}
                            className="d-block d-sm-none"
                            style={{
                                borderRadius: '6px',
                                width: '36px',
                                height: '36px',
                                padding: '0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <FaTrash size={14} />
                        </Button>
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
};

// Definición de PropTypes
ProductoCard.propTypes = {
    producto: PropTypes.shape({
        idUnica: PropTypes.string,
        dPrecioUnitario: PropTypes.number,
        dCantidad: PropTypes.number,
        vTipoFactorIVA: PropTypes.string,
        dTasaCuotaIVA: PropTypes.number,
        vCodigoBarras: PropTypes.string,
        vNombreProducto: PropTypes.string,
        vClaveUnidadSat: PropTypes.string,
        vCodigoProductoServicioSat: PropTypes.string,
        imagenURL: PropTypes.string,
        vImagenProducto_Base64: PropTypes.string
    }).isRequired,
    index: PropTypes.number.isRequired,
    actualizarCampo: PropTypes.func.isRequired,
    eliminarProducto: PropTypes.func.isRequired,
};

export default ProductoCard;